import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Popover as DSPopover } from '@digdir/designsystemet-react'
import type { Placement } from './Dropdown'

// ---------------------------------------------------------------------------
// Tooltip — accessible hover/focus label for a trigger (props in → UI out).
//
// DS Popover owns the native surface; the wrapper retains description semantics,
// configurable timing and fixed geometry. DS Tooltip's data-tooltip observer
// rewrites names/descriptions and owns a global, non-configurable hover delay.
//
// Dumb and surface-agnostic: the caller passes an already-translated `label`;
// the component holds no Norwegian, no fetching, no privileged logic — safe for
// the public, sessionless register bundle. Focus is NOT trapped (a tooltip is
// a passive description, not a dialog).
//
// Behaviour notes:
//   - opens after a short delay (skipped under prefers-reduced-motion so the
//     description is immediate for users who opt out of motion)
//   - never shows for an empty label, and never on a touch-only open (touch
//     pointers fire no hover; we suppress the synthetic mouseenter that some
//     browsers emit after a tap)
// ---------------------------------------------------------------------------

export interface TooltipProps {
  /** The description text (pre-translated by the caller). Empty → no tooltip. */
  label: string
  /** The element the tooltip describes. */
  children: ReactNode
  /** Which side the bubble anchors to. Defaults to 'top-start' → 'top'. */
  placement?: Placement
  /** Open delay in ms (suppressed under prefers-reduced-motion). Defaults 400. */
  delay?: number
}

// Gap in px between the trigger and the bubble.
const OFFSET = 8

type BubbleStyle = Pick<CSSProperties, 'position' | 'top' | 'left'>

// Honours BOTH the explicit user toggle (data-motion="reduced" on <html>) and
// the OS prefers-reduced-motion setting. Mirrors Toast.tsx; matchMedia is
// guarded because jsdom may not provide it.
function prefersReducedMotion(): boolean {
  if (typeof document !== 'undefined') {
    if (document.documentElement.getAttribute('data-motion') === 'reduced') {
      return true
    }
  }
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
  } catch {
    return false
  }
}

// Compute the fixed-position coordinates for the bubble, centred on the trigger
// along the cross axis and clamped to the viewport.
function computePosition(
  triggerRect: DOMRect,
  bubbleEl: HTMLElement | null,
  placement: Placement,
): BubbleStyle {
  const bw = bubbleEl?.offsetWidth ?? 0
  const bh = bubbleEl?.offsetHeight ?? 0
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0

  const isTop = placement === 'top-start' || placement === 'top-end'

  let top = isTop
    ? triggerRect.top - bh - OFFSET
    : triggerRect.bottom + OFFSET
  let left = triggerRect.left + triggerRect.width / 2 - bw / 2

  // Clamp into the viewport with a small margin.
  const M = 4
  if (vw > 0) left = Math.max(M, Math.min(left, vw - bw - M))
  if (vh > 0) top = Math.max(M, Math.min(top, vh - bh - M))

  return { position: 'fixed', top: Math.round(top), left: Math.round(left) }
}

/**
 * Accessible tooltip. Shows `label` on hover + focus of the wrapped trigger,
 * associates it via `aria-describedby`, and hides on leave / blur / Escape.
 *
 * @example
 * <Tooltip label={t('cases.archive.hint')}>
 *   <Button icon="archive" iconOnly aria-label={t('cases.archive')} />
 * </Tooltip>
 */
export function Tooltip({ label, children, placement = 'top-start', delay = 400 }: TooltipProps) {
  const [open, setOpen] = useState(false)
  const tipId = useId()
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const bubbleRef = useRef<HTMLDivElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchRef = useRef(false)
  const hoveredRef = useRef(false)
  const focusedRef = useRef(false)
  const [bubbleStyle, setBubbleStyle] = useState<BubbleStyle>({
    position: 'fixed',
    top: 0,
    left: 0,
  })

  const hasLabel = label.trim().length > 0

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const show = useCallback(() => {
    if (!hasLabel || touchRef.current) return
    clearTimer()
    if (open) return
    if (prefersReducedMotion() || delay <= 0) {
      setOpen(true)
      return
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setOpen(true)
    }, delay)
  }, [hasLabel, clearTimer, delay, open])

  const hide = useCallback(() => {
    clearTimer()
    setOpen(false)
  }, [clearTimer])

  // Clean up any pending timer on unmount.
  useEffect(() => clearTimer, [clearTimer])
  useEffect(() => {
    if (!hasLabel) hide()
  }, [hasLabel, hide])

  // Position before paint once the bubble has mounted; keep it pinned while
  // open against scroll/resize.
  const reposition = useCallback(() => {
    const anchorEl = anchorRef.current
    if (!anchorEl) return
    const rect = anchorEl.getBoundingClientRect()
    setBubbleStyle(computePosition(rect, bubbleRef.current, placement))
  }, [placement])

  useLayoutEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, label, reposition])

  // Escape hides the tooltip without disturbing focus.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (e.target instanceof Element && e.target.closest('dialog') !== (anchorRef.current?.closest('dialog') ?? null)) return
      if (hasLabel && bubbleRef.current?.matches(':popover-open')) {
        e.preventDefault()
        e.stopPropagation()
        bubbleRef.current.hidePopover()
      }
      hide()
    }
    // Window capture precedes owners' document capture and native dialog cancel.
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [hasLabel, hide])

  // Suppress the synthetic mouseenter some browsers emit right after a tap:
  // a touch pointer should not surface a hover-only tooltip.
  const enter = () => {
    hoveredRef.current = true
    show()
  }
  const leave = (e: React.MouseEvent) => {
    const next = e.relatedTarget
    if (next instanceof Node && (anchorRef.current?.contains(next) || bubbleRef.current?.contains(next))) return
    hoveredRef.current = false
    if (focusedRef.current) return
    clearTimer()
    // Allow crossing the physical gap to the hoverable, portalled description.
    if (open) timerRef.current = setTimeout(hide, 100)
    else hide()
  }

  // Only ARIA is cloned: caller handlers run naturally once, including focus/blur.
  // The anchor catches hover even when the child is a native disabled button.
  const visible = open && hasLabel
  const el = isValidElement(children) ? children as ReactElement<Record<string, unknown>> : null
  const originalDescription = el?.props['aria-describedby'] as string | undefined
  const describedBy = visible ? [originalDescription, tipId].filter(Boolean).join(' ') : originalDescription
  const triggerNode = el
    ? cloneElement(el, { 'aria-describedby': describedBy })
    : <span aria-describedby={describedBy}>{children}</span>

  const bubble =
    visible && typeof document !== 'undefined'
      ? createPortal(
          <DSPopover
            ref={bubbleRef}
            id={tipId}
            open
            placement="none"
            data-floating={placement.startsWith('top') ? 'top' : 'bottom'}
            onToggle={reposition}
            role="tooltip"
            className="tip"
            style={bubbleStyle}
            onMouseEnter={enter}
            onMouseLeave={leave}
          >
            {label}
          </DSPopover>,
          anchorRef.current?.closest('[popover],dialog') ?? document.body,
        )
      : null

  return (
    <span
      className="tip-anchor"
      ref={anchorRef}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocusCapture={() => { focusedRef.current = true; show() }}
      onBlurCapture={() => { focusedRef.current = false; if (!hoveredRef.current) hide() }}
      onPointerDownCapture={(e) => {
        touchRef.current = e.pointerType === 'touch'
        if (touchRef.current) hide()
      }}
      onPointerMove={(e) => {
        if (touchRef.current && (e.pointerType === 'mouse' || e.pointerType === 'pen')) {
          touchRef.current = false
          enter()
        }
      }}
    >
      {triggerNode}
      {bubble}
    </span>
  )
}
